import type { ColumnDef, SortingState } from '@tanstack/react-table';
import axios from 'axios';
import cx from 'clsx';
import { dateToLocalYMDHM } from 'common/date.ts';
import { fromQuerystring, MPOST, POST, parseFormValues, toQuerystring } from 'common/helpers.ts';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import useSWRMutation from 'swr/mutation';
import type { Role } from 'types/Role.ts';
import { Button } from 'ui/component/Button.tsx';
import { IAdd, ICopy, IDelete, ISearch } from 'ui/component/Icons.tsx';
import { Input } from 'ui/control/Input.tsx';
import { usePageSizeInUrl } from 'ui/hook/usePageSizeInUrl.ts';
import { useRestoreScrollPositionOnBack } from 'ui/hook/useRestoreScrollPositionOnBack.ts';
import { DeleteDialog } from '#admin/component/DeleteDialog.tsx';
import { useAlert, usePrompt } from '#admin/component/DialogProvider.tsx';
import { RoleForm } from '#admin/component/RoleForm.tsx';
import { getSWRKeyForPage, Table } from '#admin/component/table/index.ts';
import { getTranslatedError } from '#admin/helpers.ts';

export const getColumns = (t: any, onDelete: (role: Role) => void, onDuplicate: (role: Role) => void) => {
  const columns: ColumnDef<Role>[] = [
    {
      accessorKey: 'name',
      header: t('views.roleList.columns.name'),
      size: 200,
      cell: (info) => {
        const value = info.getValue<string>();
        if (!value) return null;
        const id = info.row.original.id;
        return (
          <Link className="anchor anchor-novisit flex flex-row items-center gap-1" to={`/role/${id}`} data-table-no-row-events>
            {value}
          </Link>
        );
      },
    },
    {
      accessorKey: 'created_at',
      header: t('views.roleList.columns.createdAt'),
      size: 170,
      cell: (info) => {
        const { created_at } = info.row.original;
        return dateToLocalYMDHM(created_at);
      },
    },
    {
      accessorKey: 'description',
      header: t('views.roleList.columns.description'),
      minSize: 500,
      meta: { truncate: true },
    },
    {
      header: t('views.roleList.columns.actions'),
      size: 70,
      meta: { hideFallback: true },
      cell: (info) => (
        <div className="flex w-full items-center justify-center">
          <ICopy
            className="text-gray-500"
            size={20}
            onClick={() => {
              if (onDuplicate) onDuplicate(info.row.original);
            }}
          />
          <IDelete
            className="text-gray-500"
            size={20}
            onClick={() => {
              if (onDelete) onDelete(info.row.original);
            }}
          />
        </div>
      ),
    },
  ];

  return columns;
};

type FormValues = {
  search: string;
};

export const RoleList = () => {
  const { t } = useTranslation();
  const prompt = usePrompt();
  const alert = useAlert();
  const formRef = React.useRef<HTMLFormElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const scrollRef = useRestoreScrollPositionOnBack('roles-scroll');
  const [openRoleForm, setOpenRoleForm] = React.useState<boolean>(false);
  const [pendingDeleteRole, setPendingDeleteRole] = React.useState<Role | null>(null);
  const [sorters, setSorters] = React.useState<SortingState>([{ id: 'created_at', desc: false }]);

  const defaultValues: FormValues = { search: '' };
  const defaultValuesWithSearchValues = { ...defaultValues, ...fromQuerystring(searchParams, { excludeKeys: ['size'] }) };
  const [values, setValues] = React.useState<typeof defaultValues>(defaultValuesWithSearchValues);

  const formContext = useForm({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: defaultValuesWithSearchValues,
  });
  const { handleSubmit, formState } = formContext;

  const { data: rowsCount } = useSWR(formState.isValid ? ['/role/count', values, parseFormValues] : null, POST);
  const swr = useSWRInfinite(getSWRKeyForPage('/role/list', 25, values, sorters, parseFormValues), POST, {
    revalidateFirstPage: false,
  });
  const setSizeInUrl = usePageSizeInUrl(swr.setSize);
  const duplicateSwr = useSWRMutation('/role', MPOST);

  const onSubmit = async (values: FormValues) => {
    setSearchParams(toQuerystring(values), { replace: true });
    setValues(values);
    await swr.mutate();
  };

  const handleAddRole = () => {
    setOpenRoleForm(true);
  };

  const handleAddRoleClose = async () => {
    setOpenRoleForm(false);
    try {
      await swr.mutate();
    } catch (error: any) {
      const message = getTranslatedError(error, t) || 'An error occurred while saving. Please try again.';
      await alert(message);
    }
  };

  const handleDeleteRoleConfirm = async () => {
    if (!pendingDeleteRole) return;
    try {
      await axios.delete(`/role/${pendingDeleteRole.id}`);
      setPendingDeleteRole(null);
      swr.mutate();
    } catch (error: any) {
      const message = getTranslatedError(error, t) || 'An error occurred while saving. Please try again.';
      setPendingDeleteRole(null);
      await alert(message);
    }
  };

  const columns = React.useMemo(
    () =>
      getColumns(
        t,
        (role) => {
          setPendingDeleteRole(role);
        },
        async (role) => {
          const name = await prompt(t('views.role.confirmMessages.duplicate'));
          if (!name) return;
          try {
            await duplicateSwr.trigger({
              name,
              description: '',
              permission_templates: btoa(JSON.stringify(role.permission_templates)),
            });
            swr.mutate();
          } catch (error: any) {
            const message = getTranslatedError(error, t) || 'An error occurred while saving. Please try again.';
            await alert(message);
          }
        },
      ),
    [swr, t, duplicateSwr, prompt, alert],
  );

  return (
    <main className="view-container">
      <RoleForm isOpen={openRoleForm} onClose={handleAddRoleClose} onSave={handleAddRoleClose} />
      <DeleteDialog
        isOpen={!!pendingDeleteRole}
        onClose={() => setPendingDeleteRole(null)}
        onConfirm={handleDeleteRoleConfirm}
        title={t('views.role.buttons.deleteRole')}
        message={t('views.role.confirmMessages.deleteRoleWithName', { name: pendingDeleteRole?.name ?? '' })}
      />
      <section className="flex flex-row place-content-between items-baseline">
        <h4>{t('views.roleList.title')}</h4>
        <div className="flex flex-row gap-4">
          <Button className="blue" LeftIcon={IAdd} onClick={handleAddRole}>
            {t('actions.add')}
          </Button>
        </div>
      </section>

      <section className="mt-4 mb-6 bg-white p-8 pb-4">
        <div>
          <FormProvider {...formContext}>
            <form id="search-form" ref={formRef} className="mb-4 flex flex-col gap-8" noValidate={true} onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              <div className="flex place-content-between place-items-center">
                <Input
                  className="w-[360px]"
                  name={'search'}
                  placeholder={t('views.roleList.search.placeholder')}
                  required={true}
                  autoFocus={true}
                  RightIcon={ISearch}
                  autoComplete={'off'}
                  debouncedAutoSubmitForm={'#search-form'}
                  debouncedAutoSubmitTimeout={500}
                />
              </div>
            </form>
          </FormProvider>
        </div>

        <Table
          scrollRef={scrollRef}
          className={cx('max-h-[calc(100vh-385px)]')}
          columns={columns}
          swr={{ ...swr, setSize: setSizeInUrl }}
          pagination={true}
          rowsCount={rowsCount}
          defaultSorting={sorters}
          onColumnSort={setSorters}
          estimateSize={50}
          overscan={50}
        />
      </section>
    </main>
  );
};
